/**
 * Job Progress Store
 *
 * Framework-agnostic Zustand store holding live job-progress snapshots, keyed by
 * `job_id`. The always-on Echo bridge writes every `.job.progress.updated`
 * event here; hooks read from it. Not persisted — job progress is transient.
 *
 * Terminal jobs are kept briefly (so a screen can read the final state) then
 * lazily garbage-collected on the next upsert. See
 * docs/prd/prd-job-progress-tracking.md §5.
 */

import { immer } from 'zustand/middleware/immer';
import { createStore } from 'zustand/vanilla';

import { registerSessionScoped } from './session-scope';
import { JOB_PROGRESS_CONSTANTS } from '../config/constants';
import { dayjs } from '../utils/date';
import {
  didPlanGenerationComplete,
  isTerminalJobStatus,
} from '../utils/job-progress.utils';

import type { JobProgress } from '../types/job-progress.types';

// Re-export for convenience
export type { JobProgress };

/** How long a terminal job stays readable before it is GC'd (ms). */
const TERMINAL_RETENTION_MS = 60 * 1000;

interface JobProgressStoreState {
  /** Live snapshots keyed by `job_id`. */
  jobs: Record<string, JobProgress>;
}

interface JobProgressStoreActions {
  /** Apply a broadcast/REST snapshot. No-op when the kill switch is off. */
  upsert: (progress: JobProgress) => void;
  /** Stop tracking a single job (e.g. once a consumer has handled it). */
  remove: (jobId: string) => void;
  /** Drop every tracked job. */
  reset: () => void;
}

export type JobProgressStore = JobProgressStoreState & JobProgressStoreActions;

export const jobProgressStore = createStore<JobProgressStore>()(
  immer((set) => ({
    jobs: {},

    upsert: (progress: JobProgress): void => {
      // Respect the kill switch on the ingest path, mirroring the notification store.
      if (!JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS) return;
      if (!progress || typeof progress.job_id !== 'string') return;

      set((state) => {
        state.jobs[progress.job_id] = progress;

        // Lazy GC: prune terminal jobs that have aged past the retention window.
        const cutoff = dayjs().valueOf() - TERMINAL_RETENTION_MS;
        for (const [id, job] of Object.entries(state.jobs)) {
          if (id === progress.job_id) continue;
          if (
            isTerminalJobStatus(job.status) &&
            dayjs(job.updated_at).valueOf() < cutoff
          ) {
            delete state.jobs[id];
          }
        }
      });
    },

    remove: (jobId: string): void => {
      set((state) => {
        delete state.jobs[jobId];
      });
    },

    reset: (): void => {
      set((state) => {
        state.jobs = {};
      });
    },
  }))
);

/**
 * Call `listener` each time a personalised plan finishes generating. What a
 * cache of plans, workouts or the dashboard listens to, so a list already on
 * screen picks the new plan up without waiting out its cache.
 *
 * @returns Unsubscribe
 */
export function subscribeToPlanGenerated(listener: () => void): () => void {
  return jobProgressStore.subscribe((state, previous) => {
    if (didPlanGenerationComplete(state.jobs, previous.jobs)) listener();
  });
}

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('jobProgressStore', () => {
  jobProgressStore.getState().reset();
});
